/**
 * 版本检测器
 *
 * 定期轮询 version.json，检测服务器是否有新版本部署，
 * 若检测到更新则通过回调提示用户刷新页面。
 *
 * 使用方式：在 useVersionCheck hook 中调用 startVersionCheck()。
 * version.json 由 vite 构建插件在产物根目录生成（见 vite.config.ts）。
 */

import { env } from './env';

interface VersionInfo {
  version: string;
  buildTime: string;
  gitHash: string;
}

const POLL_INTERVAL = 2 * 60 * 1000; // 2 分钟

let timer: ReturnType<typeof setInterval> | null = null;
let delayTimer: ReturnType<typeof setTimeout> | null = null;
let currentVersion: VersionInfo | null = null;
let onUpdateCallback: (() => void) | null = null;

// 产物挂载在 router basename 下（dev: /，prod: /3d-explorer），
// version.json 位于产物根目录，因此用绝对路径避免受客户端路由影响。
function versionUrl(): string {
  const base = env.VITE_ROUTER_BASENAME.replace(/\/$/, '');
  return `${base}/version.json`;
}

async function fetchVersion(): Promise<VersionInfo | null> {
  try {
    const response = await fetch(versionUrl(), {
      cache: 'no-cache',
      headers: { 'Cache-Control': 'no-cache' },
    });
    if (!response.ok) return null;
    return (await response.json()) as VersionInfo;
  } catch {
    return null;
  }
}

async function checkUpdate(): Promise<boolean> {
  const serverVersion = await fetchVersion();
  if (!serverVersion) return false;

  // 首次拉取仅记录基线，不视为更新
  if (!currentVersion) {
    currentVersion = serverVersion;
    return false;
  }

  const hasUpdate =
    currentVersion.version !== serverVersion.version ||
    currentVersion.gitHash !== serverVersion.gitHash;

  if (hasUpdate) {
    console.info('[VersionChecker] 检测到新版本', {
      current: currentVersion,
      server: serverVersion,
    });
  }

  return hasUpdate;
}

export function startVersionCheck(onUpdate: () => void): void {
  // dev 模式没有 version.json，跳过轮询
  if (import.meta.env.DEV) return;

  // 防重入：先清理可能存在的旧 timer
  stopVersionCheck();

  onUpdateCallback = onUpdate;

  // 首次延迟 10 秒检测，避免与首屏加载抢资源
  delayTimer = setTimeout(async () => {
    delayTimer = null;
    if (await checkUpdate()) {
      stopVersionCheck();
      onUpdateCallback?.();
    }
  }, 10_000);

  // 定期轮询
  timer = setInterval(async () => {
    if (await checkUpdate()) {
      stopVersionCheck();
      onUpdateCallback?.();
    }
  }, POLL_INTERVAL);

  // 页面从后台切回时检测一次
  document.addEventListener('visibilitychange', onVisibilityChange);
}

export function stopVersionCheck(): void {
  if (delayTimer) {
    clearTimeout(delayTimer);
    delayTimer = null;
  }
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
  document.removeEventListener('visibilitychange', onVisibilityChange);
}

// 同步包装：事件监听器期望返回 void，异步检测用 void 显式丢弃 Promise，
// 同时保持函数引用稳定以便 removeEventListener 正确解绑。
function onVisibilityChange(): void {
  if (document.visibilityState !== 'visible') return;
  void runUpdateCheck();
}

async function runUpdateCheck(): Promise<void> {
  if (await checkUpdate()) {
    stopVersionCheck();
    onUpdateCallback?.();
  }
}
